import type { Notification } from "./types";

export type NotificationGroup =
  | "ORDERS"
  | "SHIPMENT"
  | "LOGISTICS"
  | "PAYMENTS"
  | "CASH"
  | "STORE"
  | "ACCOUNT"
  | "SUBSCRIPTION";

export type NotificationTone = "INFO" | "SUCCESS" | "WARNING" | "ERROR";

/** Lucide icon name — the widget maps this to a component. */
export type NotificationIconName = string;

export interface NotificationContentEntry {
  group: NotificationGroup;
  icon: NotificationIconName;
  /** Per-type severity tone; wins over data.notificationType (see resolveTone). */
  tone?: NotificationTone;
}

/** Canonicalise any incoming type: upper-case, collapse [.\-\s]+ to "_". */
export function normalizeType(type: string): string {
  return (type ?? "")
    .trim()
    .replace(/[.\-\s]+/g, "_")
    .toUpperCase();
}

export const NOTIFICATION_CONTENT: Record<string, NotificationContentEntry> = {
  // ORDERS
  ORDER_CREATED: { group: "ORDERS", icon: "cart", tone: "SUCCESS" },
  ORDER_CANCELLED: { group: "ORDERS", icon: "x-circle", tone: "ERROR" },
  ORDER_ITEM_CANCELLED: { group: "ORDERS", icon: "x-circle", tone: "ERROR" },
  ORDER_DELIVERED: { group: "ORDERS", icon: "package-check", tone: "SUCCESS" },
  ORDER_SHIPPED: { group: "ORDERS", icon: "truck", tone: "SUCCESS" },
  ORDER_STATUS_CHANGED: { group: "ORDERS", icon: "clipboard-list", tone: "INFO" },
  ORDER_STATUS_REVERTED: { group: "ORDERS", icon: "rotate-ccw", tone: "WARNING" },
  ORDER_PAYMENT_FAILED: { group: "ORDERS", icon: "credit-card", tone: "ERROR" },
  ORDER_REFUND_ISSUED: { group: "ORDERS", icon: "rotate-ccw", tone: "INFO" },
  ORDER_REFUND_SETTLED: { group: "ORDERS", icon: "circle-check", tone: "SUCCESS" },
  ORDER_REFUND_OVERDUE: { group: "ORDERS", icon: "x-circle", tone: "ERROR" },

  // RETURNS (grouped with ORDERS — the mock lists them alongside order
  // notifications rather than as a separate tab)
  RETURN_REQUESTED: { group: "ORDERS", icon: "refresh", tone: "INFO" },
  RETURN_APPROVED: { group: "ORDERS", icon: "refresh", tone: "SUCCESS" },
  RETURN_REJECTED: { group: "ORDERS", icon: "x-circle", tone: "WARNING" },
  RETURN_RECEIVED: { group: "ORDERS", icon: "refresh", tone: "INFO" },
  // RETURN_COMPLETED has no entry of its own — resolveVariantKey always
  // resolves it to one of these two before lookup (see resolveVariantKey).
  RETURN_COMPLETED_REFUND_DUE: { group: "ORDERS", icon: "alert-triangle", tone: "WARNING" },
  RETURN_COMPLETED_NO_REFUND: { group: "ORDERS", icon: "refresh", tone: "SUCCESS" },
  RETURN_EXPIRED: { group: "ORDERS", icon: "refresh", tone: "SUCCESS" },
  // RETURN_CANCELLED covers the "otherwise" case; the AFTER_APPROVAL variant
  // is the one worth flagging (a parcel the merchant expected isn't coming).
  RETURN_CANCELLED: { group: "ORDERS", icon: "refresh", tone: "SUCCESS" },
  RETURN_CANCELLED_AFTER_APPROVAL: { group: "ORDERS", icon: "refresh", tone: "WARNING" },

  // SHIPMENT
  SHIPMENT_CREATED: { group: "SHIPMENT", icon: "package-plus", tone: "INFO" },
  SHIPMENT_DELIVERY_FAILED: { group: "SHIPMENT", icon: "package-x", tone: "ERROR" },
  SHIPMENT_PICKUP_SCHEDULED: { group: "SHIPMENT", icon: "calendar-clock", tone: "INFO" },
  SHIPMENT_RETURNED: { group: "SHIPMENT", icon: "undo", tone: "WARNING" },

  // LOGISTICS
  LOGISTICS_COMMENT_ADDED: { group: "LOGISTICS", icon: "message-square", tone: "INFO" },
  LOGISTICS_PROVIDER_CONNECTED: { group: "LOGISTICS", icon: "truck", tone: "SUCCESS" },
  LOGISTICS_PROVIDER_ENABLED: { group: "LOGISTICS", icon: "truck", tone: "INFO" },
  LOGISTICS_PROVIDER_DISABLED: { group: "LOGISTICS", icon: "power-off", tone: "WARNING" },
  LOGISTICS_STATUS_CHANGED: { group: "LOGISTICS", icon: "truck", tone: "INFO" },

  // PAYMENTS
  PAYMENT_METHOD_CONNECTED: { group: "PAYMENTS", icon: "credit-card", tone: "SUCCESS" },
  PAYMENT_METHOD_ENABLED: { group: "PAYMENTS", icon: "credit-card", tone: "SUCCESS" },
  PAYMENT_METHOD_DISABLED: { group: "PAYMENTS", icon: "x-circle", tone: "ERROR" },
  FONEPAY_KEY_CONFIRMED: { group: "PAYMENTS", icon: "qr-code", tone: "SUCCESS" },
  FONEPAY_PUBLIC_KEY_SUBMITTED: { group: "PAYMENTS", icon: "credit-card", tone: "WARNING" },

  // CASH
  CASH_TRANSACTION_DISCREPANCY_CREATED: { group: "CASH", icon: "wallet", tone: "WARNING" },
  CASH_TRANSACTION_DISCREPANCY_RESOLVED: { group: "CASH", icon: "circle-check", tone: "SUCCESS" },

  // STORE
  STORE_CREATED: { group: "STORE", icon: "store", tone: "SUCCESS" },
  STOREFRONT_PUBLISHED: { group: "STORE", icon: "globe", tone: "SUCCESS" },
  STOREFRONT_UPDATED: { group: "STORE", icon: "refresh", tone: "INFO" },

  // ACCOUNT
  INVITE_ACCEPTED: { group: "ACCOUNT", icon: "user-plus", tone: "SUCCESS" },
  CONTACT_FORM_SUBMITTED: { group: "ACCOUNT", icon: "mail", tone: "INFO" },
  NEWSLETTER_SUBSCRIBED: { group: "ACCOUNT", icon: "user-plus", tone: "INFO" },
  USER_SUSPENDED: { group: "ACCOUNT", icon: "shield-alert", tone: "WARNING" },

  // SUBSCRIPTION
  SUBSCRIPTION_ACTIVATED: { group: "SUBSCRIPTION", icon: "crown", tone: "SUCCESS" },
  SUBSCRIPTION_REACTIVATED: { group: "SUBSCRIPTION", icon: "crown", tone: "SUCCESS" },
  SUBSCRIPTION_RENEWED: { group: "SUBSCRIPTION", icon: "crown", tone: "SUCCESS" },
  SUBSCRIPTION_RENEWAL_REMINDER_7D: { group: "SUBSCRIPTION", icon: "crown", tone: "WARNING" },
  SUBSCRIPTION_RENEWAL_REMINDER_1D: { group: "SUBSCRIPTION", icon: "crown", tone: "WARNING" },
  SUBSCRIPTION_PLAN_UPDATED: { group: "SUBSCRIPTION", icon: "crown", tone: "INFO" },
  SUBSCRIPTION_UPGRADED: { group: "SUBSCRIPTION", icon: "crown", tone: "SUCCESS" },
  SUBSCRIPTION_CANCELLED: { group: "SUBSCRIPTION", icon: "x-circle", tone: "ERROR" },
  SUBSCRIPTION_EXPIRED: { group: "SUBSCRIPTION", icon: "crown", tone: "ERROR" },
  SUBSCRIPTION_PAYMENT_FAILED: { group: "SUBSCRIPTION", icon: "x-circle", tone: "ERROR" },
  SUBSCRIPTION_TRIAL_STARTED: { group: "SUBSCRIPTION", icon: "crown", tone: "INFO" },
  SUBSCRIPTION_TRIAL_ENDING_SOON: { group: "SUBSCRIPTION", icon: "crown", tone: "WARNING" },
  SUBSCRIPTION_TRIAL_ENDING_FINAL: { group: "SUBSCRIPTION", icon: "crown", tone: "WARNING" },
  SUBSCRIPTION_TRIAL_EXPIRED: { group: "SUBSCRIPTION", icon: "crown", tone: "ERROR" },
  SUBSCRIPTION_TRIAL_FEEDBACK: { group: "SUBSCRIPTION", icon: "message-square", tone: "INFO" },
};

export const GROUP_DEFAULT_ICON: Record<NotificationGroup, NotificationIconName> = {
  ORDERS: "cart",
  SHIPMENT: "truck",
  LOGISTICS: "truck",
  PAYMENTS: "credit-card",
  CASH: "wallet",
  STORE: "store",
  ACCOUNT: "user",
  SUBSCRIPTION: "crown",
};

/**
 * Canonical variant key for a notification: the normalized type, refined
 * using payload details for types whose icon/tone/copy differ by outcome.
 * Copy (resolveCopyKey), icon and tone all resolve from this single key so
 * the three never drift out of sync with each other.
 */
export function resolveVariantKey(n: Notification): string {
  const key = normalizeType(n.type);
  const details = n.data?.details as Record<string, unknown> | undefined;
  const cancelledBy = details?.cancelledBy;
  const isCustomer =
    typeof cancelledBy === "string" && cancelledBy.toUpperCase() === "CUSTOMER";

  if (key === "ORDER_CANCELLED" && isCustomer) return "ORDER_CANCELLED_BY_CUSTOMER";
  if (key === "ORDER_ITEM_CANCELLED" && isCustomer) return "ORDER_ITEM_CANCELLED_BY_CUSTOMER";
  if (key === "RETURN_COMPLETED") {
    return Number(details?.refundAmount) > 0
      ? "RETURN_COMPLETED_REFUND_DUE"
      : "RETURN_COMPLETED_NO_REFUND";
  }
  if (key === "RETURN_CANCELLED" && details?.wasApproved === true) {
    return "RETURN_CANCELLED_AFTER_APPROVAL";
  }
  return key;
}

function entryFor(n: Notification): NotificationContentEntry | undefined {
  // Try the variant key first (e.g. RETURN_COMPLETED_REFUND_DUE); fall back
  // to the raw type for variants that share their base type's icon/tone
  // (e.g. ORDER_CANCELLED_BY_CUSTOMER has no entry of its own).
  return (
    NOTIFICATION_CONTENT[resolveVariantKey(n)] ??
    NOTIFICATION_CONTENT[normalizeType(n.type)]
  );
}

export function resolveGroup(n: Notification): NotificationGroup | null {
  return entryFor(n)?.group ?? null;
}

export function resolveIconName(n: Notification): NotificationIconName {
  const entry = entryFor(n);
  if (entry) return entry.icon;
  return "bell";
}

// Registry tone wins over data.notificationType: the backend currently
// stamps INFO on nearly every event, which would flatten every icon to
// blue. The per-type registry tone is what differentiates severity;
// the backend tone only drives types we don't know about.
export function resolveTone(n: Notification): NotificationTone {
  const entry = entryFor(n);
  if (entry?.tone) return entry.tone;
  const raw = n.data?.notificationType ?? "INFO";
  // The canonical contract value is DANGER; NotificationTone has no such
  // key (TONE_BG/TONE_FG would render undefined) — map it to ERROR.
  return raw === "DANGER" ? "ERROR" : raw;
}
